import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import Modal, { ModalRef } from '@/components/@IntranetLibrary/Modal';
import ZoomOutIcon from '@/public/icons/approval/zoom-out.svg';
import ZoomInIcon from '@/public/icons/approval/zoom-in.svg';
import FullscreenIcon from '@/public/icons/approval/fullscreen.svg';
import {
  Point,
  clamp,
  getTouchPoint,
  getTouchesCenter,
  getTouchesDistance,
  toLocalPoint,
} from '@/utils/touch';
import { imageZoomAtom } from './imageZoomAtom';
import {
  ZoomButtonBar,
  ZoomImage,
  ZoomViewerBody,
  zoomModalSx,
} from './styles';

/** 줌 한계 (레거시 동일). */
const ZOOM_MIN = 10;
const ZOOM_MAX = 400;
const ZOOM_STEP = 5;
const ZOOM_DEFAULT = 100;

/**
 * 높이 맞춤(판독 우선)으로 전환하는 조건.
 * - WIDE_IMAGE_ASPECT: 가로로 긴 "띠" 형태 문서만 대상. 16:9 스크린샷이나
 *   일반 사진은 제외해 전체를 먼저 보여주는 기존 동작을 유지한다.
 * - WIDE_FIT_MIN_ZOOM: 그 상태로 폭에 맞추면 글씨를 읽을 수 없는 경우.
 * 둘을 모두 만족할 때만 높이 맞춤으로 띄우고 좌우 pan 으로 훑게 한다.
 */
const WIDE_IMAGE_ASPECT = 2.5;
const WIDE_FIT_MIN_ZOOM = 50;

/** 핀치 시작 시점 스냅샷 — 진행 중 배율/위치 계산의 기준. */
type PinchState = {
  /** 두 손가락 시작 거리(px) */
  startDistance: number;
  /** 시작 배율(%) */
  startZoom: number;
  /** 시작 시점 두 손가락 중점(뷰어 내부 좌표) */
  startCenter: Point;
  /** 시작 시점 이미지 중심(뷰어 내부 좌표) */
  startLeft: number;
  startTop: number;
};

/** 줌 컨트롤 버튼 위의 터치 — 제스처 대상에서 제외. */
const isControlTouch = (target: EventTarget | null) =>
  target instanceof Element && !!target.closest('button');

/**
 * 본문/코멘트 이미지 "크게보기" 모달 — imageZoomAtom 구독, 포탈 표준 Modal 을 ref 로 제어.
 * PC 는 휠 줌 + 마우스 드래그 pan, 모바일은 한 손가락 pan + 두 손가락 핀치 줌.
 * 열려 있는 동안 터치 기본 동작을 막아 스와이프 뒤로가기로 문서에서 이탈하지 않게 한다.
 */
const ImageZoomModal = () => {
  const { t } = useTranslation();
  const [{ open, src }, setImageZoom] = useAtom(imageZoomAtom);

  const modalRef = useRef<ModalRef>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const viewerRef = useRef<HTMLDivElement | null>(null);
  // 휠/드래그/터치 effect 트리거용 — MUI Modal 은 open() 후에야 children 을 mount 하므로
  // atom open 만으론 ref 가 null. 실제 mount/unmount 시 갱신되는 state 로 effect 재실행.
  const [viewerNode, setViewerNode] = useState<HTMLDivElement | null>(null);
  const setViewerRef = useCallback((node: HTMLDivElement | null) => {
    viewerRef.current = node;
    setViewerNode(node);
  }, []);

  // 레거시 nowZoom(%) — 직접 style 조작용 ref.
  const nowZoomRef = useRef<number>(ZOOM_DEFAULT);
  // 드래그(pan) 추적 — 마우스/터치 공용.
  const draggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0, left: 0, top: 0 });
  // 핀치 줌 추적.
  const pinchRef = useRef<PinchState | null>(null);

  // 버튼 disabled 만 state. (렌더 트리거)
  const [zoomOutDisabled, setZoomOutDisabled] = useState(false);
  const [zoomInDisabled, setZoomInDisabled] = useState(false);

  /** 이미지가 뷰어 밖으로 빠져나가지 않게 중심 좌표를 제한 — 넘치지 않는 축은 가운데 고정. */
  const clampPosition = useCallback((left: number, top: number) => {
    const img = imgRef.current;
    const viewer = viewerRef.current;
    if (!img || !viewer) return { left, top };
    const scale = nowZoomRef.current / 100;
    const halfOverflowX = Math.max(
      0,
      (img.offsetWidth * scale - viewer.clientWidth) / 2,
    );
    const halfOverflowY = Math.max(
      0,
      (img.offsetHeight * scale - viewer.clientHeight) / 2,
    );
    const centerX = viewer.clientWidth / 2;
    const centerY = viewer.clientHeight / 2;
    return {
      left: clamp(left, centerX - halfOverflowX, centerX + halfOverflowX),
      top: clamp(top, centerY - halfOverflowY, centerY + halfOverflowY),
    };
  }, []);

  /** pan 위치를 제한 범위 안에서 이미지에 반영. */
  const applyPosition = useCallback(
    (left: number, top: number) => {
      const img = imgRef.current;
      if (!img) return;
      const next = clampPosition(left, top);
      img.style.left = `${next.left}px`;
      img.style.top = `${next.top}px`;
    },
    [clampPosition],
  );

  /** nowZoom 을 이미지 transform 에 반영 + 위치 재제한 + 버튼 disabled 동기화. */
  const updateZoom = useCallback(() => {
    const img = imgRef.current;
    const z = nowZoomRef.current;
    if (img) {
      img.style.transform = `translate(-50%, -50%) scale(${z / 100})`;
      // 축소 시 이미지가 한쪽으로 치우친 채 남지 않도록 위치도 다시 제한.
      applyPosition(img.offsetLeft, img.offsetTop);
    }
    setZoomOutDisabled(z <= ZOOM_MIN);
    setZoomInDisabled(z >= ZOOM_MAX);
  }, [applyPosition]);

  const zoomIn = useCallback(() => {
    if (nowZoomRef.current < ZOOM_MAX) {
      nowZoomRef.current = Math.min(nowZoomRef.current + ZOOM_STEP, ZOOM_MAX);
      updateZoom();
    }
  }, [updateZoom]);

  const zoomOut = useCallback(() => {
    if (nowZoomRef.current > ZOOM_MIN) {
      nowZoomRef.current = Math.max(nowZoomRef.current - ZOOM_STEP, ZOOM_MIN);
      updateZoom();
    }
  }, [updateZoom]);

  const zoomReset = useCallback(() => {
    nowZoomRef.current = ZOOM_DEFAULT;
    updateZoom();
  }, [updateZoom]);

  /** 위치(pan) + 줌을 기본값으로 리셋 (닫을 때). */
  const resetViewerState = useCallback(() => {
    const img = imgRef.current;
    if (img) {
      img.style.left = '50%';
      img.style.top = '50%';
      img.classList.remove('dragging', 'gesturing');
    }
    nowZoomRef.current = ZOOM_DEFAULT;
    draggingRef.current = false;
    pinchRef.current = null;
    setZoomOutDisabled(false);
    setZoomInDisabled(false);
  }, []);

  /**
   * 이미지 load 시 초기 배율 — 기본은 레거시와 같은 폭 맞춤(원본 100% 상한).
   * 가로로 긴 리포트 이미지만 예외로 높이 맞춤을 써서 좌우 pan 으로 읽게 한다.
   */
  const handleImageLoad = useCallback(() => {
    const img = imgRef.current;
    const viewer = viewerRef.current;
    if (!img || !viewer) return;
    const { naturalWidth, naturalHeight } = img;
    if (naturalWidth > 0 && naturalHeight > 0) {
      const fitWidthZoom = (viewer.clientWidth / naturalWidth) * 100;
      const fitHeightZoom = (viewer.clientHeight / naturalHeight) * 100;
      const isWideReport =
        naturalWidth / naturalHeight >= WIDE_IMAGE_ASPECT &&
        fitWidthZoom < WIDE_FIT_MIN_ZOOM;
      const fitZoom = isWideReport
        ? Math.max(fitWidthZoom, fitHeightZoom)
        : fitWidthZoom;
      nowZoomRef.current = clamp(
        Math.min(fitZoom, ZOOM_DEFAULT),
        ZOOM_MIN,
        ZOOM_MAX,
      );
    } else {
      nowZoomRef.current = ZOOM_DEFAULT;
    }
    updateZoom();
  }, [updateZoom]);

  /** atom open 변화에 따라 포탈 Modal 을 ref 로 제어. */
  useEffect(() => {
    if (!modalRef.current) return;
    if (open) {
      if (!modalRef.current.isOpen) modalRef.current.open();
    } else {
      if (modalRef.current.isOpen) modalRef.current.close();
    }
  }, [open]);

  /** Modal X/배경 클릭 → atom 닫기 + 뷰어 상태 리셋. */
  const handleClose = useCallback(() => {
    resetViewerState();
    setImageZoom({ open: false, src: null });
  }, [resetViewerState, setImageZoom]);

  // ===== 휠 / 마우스 드래그 / 터치: SSR 안전하게 useEffect 안에서 등록 =====
  // viewerNode 의존 — open 만 의존하면 Modal 열리기 전 ref 가 null 이라 등록 누락.
  useEffect(() => {
    if (!open) return;
    const viewer = viewerNode;
    const img = imgRef.current;
    if (!viewer || !img) return;

    // 휠 줌 (preventDefault → passive:false)
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (e.deltaY * -0.005 > 0) {
        zoomIn();
      } else {
        zoomOut();
      }
    };

    // 드래그(pan)
    const onMouseDown = (e: MouseEvent) => {
      draggingRef.current = true;
      img.classList.add('dragging');
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        left: img.offsetLeft,
        top: img.offsetTop,
      };
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!draggingRef.current) return;
      const { x, y, left, top } = dragStartRef.current;
      applyPosition(left + (e.clientX - x), top + (e.clientY - y));
    };
    const onMouseUp = () => {
      draggingRef.current = false;
      img.classList.remove('dragging');
    };
    const onDragStart = (e: Event) => e.preventDefault();

    // 터치 pan — 시작 지점과 그때의 이미지 중심을 기준점으로 잡는다.
    const startPan = (touch: Touch) => {
      const { x, y } = getTouchPoint(touch);
      draggingRef.current = true;
      dragStartRef.current = { x, y, left: img.offsetLeft, top: img.offsetTop };
    };

    // 핀치 줌 — 두 손가락 거리/중점과 그때의 배율·위치를 스냅샷.
    const startPinch = (a: Touch, b: Touch) => {
      const rect = viewer.getBoundingClientRect();
      pinchRef.current = {
        startDistance: getTouchesDistance(a, b) || 1, // 0 나눗셈 방지
        startZoom: nowZoomRef.current,
        startCenter: toLocalPoint(getTouchesCenter(a, b), rect),
        startLeft: img.offsetLeft,
        startTop: img.offsetTop,
      };
    };

    const onTouchStart = (e: TouchEvent) => {
      if (isControlTouch(e.target)) return;
      // 뷰어 안에서는 브라우저 기본 터치 동작(스와이프 뒤로가기/스크롤)을 막는다.
      if (e.cancelable) e.preventDefault();
      // 제스처 중에는 transform 트랜지션을 꺼서 손가락을 그대로 따라오게 한다.
      img.classList.add('gesturing');
      if (e.touches.length >= 2) {
        draggingRef.current = false;
        startPinch(e.touches[0], e.touches[1]);
      } else {
        pinchRef.current = null;
        startPan(e.touches[0]);
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (isControlTouch(e.target)) return;
      if (e.cancelable) e.preventDefault();

      const pinch = pinchRef.current;
      if (pinch && e.touches.length >= 2) {
        const rect = viewer.getBoundingClientRect();
        const distance = getTouchesDistance(e.touches[0], e.touches[1]);
        const center = toLocalPoint(
          getTouchesCenter(e.touches[0], e.touches[1]),
          rect,
        );
        const nextZoom = clamp(
          (distance / pinch.startDistance) * pinch.startZoom,
          ZOOM_MIN,
          ZOOM_MAX,
        );
        const ratio = nextZoom / pinch.startZoom;
        nowZoomRef.current = nextZoom;
        // 두 손가락 중점 아래의 이미지 지점이 그 자리에 머물도록 중심을 보정.
        // (updateZoom 이 이어서 pan 범위를 다시 제한한다.)
        img.style.left = `${
          center.x - (pinch.startCenter.x - pinch.startLeft) * ratio
        }px`;
        img.style.top = `${
          center.y - (pinch.startCenter.y - pinch.startTop) * ratio
        }px`;
        updateZoom();
        return;
      }

      if (draggingRef.current && e.touches.length === 1) {
        const { x, y } = getTouchPoint(e.touches[0]);
        const { x: startX, y: startY, left, top } = dragStartRef.current;
        applyPosition(left + (x - startX), top + (y - startY));
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length === 0) {
        draggingRef.current = false;
        pinchRef.current = null;
        img.classList.remove('gesturing');
        return;
      }
      // 손가락을 떼면 남은 손가락 구성으로 기준점을 다시 잡는다.
      // (재설정하지 않으면 손가락 3개 이상에서 하나를 뗄 때 배율이 튄다.)
      if (e.touches.length === 1) {
        pinchRef.current = null;
        startPan(e.touches[0]);
        return;
      }
      draggingRef.current = false;
      startPinch(e.touches[0], e.touches[1]);
    };

    // 모달 헤더처럼 뷰어 밖이지만 모달 안인 영역에서 시작한 스와이프도 막는다.
    // 모달 루트로 범위를 한정해 바깥 화면의 터치 동작에는 영향을 주지 않는다.
    const modalRoot = viewer.closest('.modal-inner');
    const blockModalTouchMove = (e: TouchEvent) => {
      if (isControlTouch(e.target)) return;
      if (e.cancelable) e.preventDefault();
    };

    // 회전/리사이즈로 뷰어 크기가 바뀌면 pan 허용 범위도 달라지므로 위치를 다시 맞춘다.
    const onResize = () => updateZoom();

    viewer.addEventListener('wheel', onWheel, { passive: false });
    img.addEventListener('mousedown', onMouseDown);
    img.addEventListener('dragstart', onDragStart);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    viewer.addEventListener('touchstart', onTouchStart, { passive: false });
    viewer.addEventListener('touchmove', onTouchMove, { passive: false });
    viewer.addEventListener('touchend', onTouchEnd);
    viewer.addEventListener('touchcancel', onTouchEnd);
    modalRoot?.addEventListener(
      'touchmove',
      blockModalTouchMove as EventListener,
      {
        passive: false,
      },
    );
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    // 뷰어가 열려 있는 동안만 브라우저 오버스크롤 히스토리 이동을 끈다.
    const { style: rootStyle } = document.documentElement;
    const prevOverscrollBehaviorX = rootStyle.overscrollBehaviorX;
    rootStyle.overscrollBehaviorX = 'none';

    return () => {
      viewer.removeEventListener('wheel', onWheel);
      img.removeEventListener('mousedown', onMouseDown);
      img.removeEventListener('dragstart', onDragStart);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      viewer.removeEventListener('touchstart', onTouchStart);
      viewer.removeEventListener('touchmove', onTouchMove);
      viewer.removeEventListener('touchend', onTouchEnd);
      viewer.removeEventListener('touchcancel', onTouchEnd);
      modalRoot?.removeEventListener(
        'touchmove',
        blockModalTouchMove as EventListener,
      );
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      rootStyle.overscrollBehaviorX = prevOverscrollBehaviorX;
    };
  }, [open, src, viewerNode, zoomIn, zoomOut, applyPosition, updateZoom]);

  return (
    <Modal
      ref={modalRef}
      title={t('이미지 크게보기')}
      size="expand"
      isBlockBackDropClose={false}
      noBodyPadding
      handleClose={handleClose}
      sx={zoomModalSx}
    >
      <ZoomViewerBody ref={setViewerRef}>
        {src && (
          <>
            <ZoomImage
              ref={imgRef}
              src={src}
              alt={t('이미지 크게보기')}
              onLoad={handleImageLoad}
              draggable={false}
            />
            <ZoomButtonBar>
              <ul>
                <li>
                  <button
                    type="button"
                    onClick={zoomOut}
                    disabled={zoomOutDisabled}
                    aria-label={t('축소')}
                  >
                    <ZoomOutIcon />
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={zoomIn}
                    disabled={zoomInDisabled}
                    aria-label={t('확대')}
                  >
                    <ZoomInIcon />
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={zoomReset}
                    aria-label={t('원본 크기')}
                  >
                    <FullscreenIcon />
                  </button>
                </li>
              </ul>
            </ZoomButtonBar>
          </>
        )}
      </ZoomViewerBody>
    </Modal>
  );
};

export default ImageZoomModal;
